// Lib
import { get } from 'lodash/fp';

// Utils
import { isEmpty } from '../../../common/utils/immutableHelper';
import { depthFirstTraversal } from '../../../common/dataStructures/graphUtils';
import { getElement } from '../../../common/elements/utils/elementTraversalUtils';
import { sortByScoreProperty } from '../../../common/elements/utils/elementSortUtils';
import { buildOrderedSubGraph } from '../../../common/elements/utils/elementGraphUtils';
import { getElementId, getElementType } from '../../../common/elements/utils/elementPropertyUtils';
import { getMainEditorId, getMainEditorKey } from '../../element/utils/elementEditorUtils';

// Selectors
import { getElements } from '../../element/selectors/elementSelector';
import { getShowTitle } from '../../element/taskList/taskListSelector';
import { activeModalIdSelector } from '../../components/modal/activeModalSelector';
import { elementGraphSelector } from '../../element/selectors/elementGraphSelector';
import { activePopupSelector } from '../../components/popupPanel/popupOpenSelector';
import { getSelectedElements } from '../../element/selection/selectedElementsSelector';
import { getCurrentlyEditingId } from '../../element/selection/currentlyEditingSelector';
import { getCurrentBoardIdFromState } from '../../reducers/currentBoardId/currentBoardIdSelector';

// Actions
import { navigateToElement } from '../../reducers/navigationActions';
import { startEditingComment } from '../../element/comment/store/localCommentActions';
import { startEditingElement, startEditingElementWithDelay } from '../../element/selection/selectionActions';
import { createAndEditElement, updateSelectedElements } from '../../element/actions/elementActions';

// Constants
import { SHOW_TITLE_PROPERTY } from '../../../common/taskLists/taskListConstants';
import { BoardSections } from '../../../common/boards/boardConstants';
import { ELEMENT_CREATION_SOURCES } from '../../../common/elements/elementConstants';
import { isTable } from '../../../common/elements/utils/elementTypeUtils';
import { ElementType } from '../../../common/elements/elementTypes';

/**
 * When starting to edit tasks, choose the last task in the list.
 */
const handleEditTask = (element) => (dispatch, getState) => {
    const state = getState();
    const elements = getElements(state);

    const elementId = getElementId(element);

    const elementGraph = elementGraphSelector(state);
    const taskListGraph = buildOrderedSubGraph({
        elements,
        elementGraph,
        startElementId: elementId,
        elementSortFn: sortByScoreProperty,
    });
    const dfsResult = depthFirstTraversal(taskListGraph, elementId);

    const lastTaskId = get('id', dfsResult[dfsResult.length - 1]);
    const lastTask = getElement(elements, lastTaskId);

    return dispatch(
        startEditingElement({
            id: lastTaskId,
            editorId: getMainEditorId({ element: lastTask }),
            editorKey: getMainEditorKey({ element: lastTask }),
        }),
    );
};

/**
 * Comment threads should start editing the reply form if it exists, or change to that state.
 */
const handleEditCommentThread = (element) => (dispatch, getState) => {
    const elementId = getElementId(element);

    dispatch(startEditingComment({ _id: elementId, elementId }));
    return dispatch(
        startEditingElement({
            id: elementId,
            editorId: getMainEditorId({ element }),
            editorKey: getMainEditorKey({ element }),
        }),
    );
};

const handleElementEdit = (element) => (dispatch, getState) => {
    const elementId = getElementId(element);
    const elementType = getElementType(element);

    switch (elementType) {
        // Images should open their modal
        case ElementType.IMAGE_TYPE:
            return dispatch(navigateToElement({ elementId }));
        // Comments should edit if awaiting editing, otherwise create a new reply
        // Tasks should start editing their last entry
        case ElementType.TASK_LIST_TYPE:
            return dispatch(handleEditTask(element));
        // Boards and columns should start editing their title
        case ElementType.COMMENT_THREAD_TYPE:
            return dispatch(handleEditCommentThread(element));
        // Links should start editing their title
        // Cards should start editing the main text block
        default:
            return dispatch(
                startEditingElement({
                    id: elementId,
                    editorId: getMainEditorId({ element }),
                    editorKey: getMainEditorKey({ element }),
                }),
            );
    }
};

/**
 * Start editing an element if a popup or modal isn't active.
 */
export const startEditingSelectedElementOnReturn = () => (dispatch, getState) => {
    const state = getState();

    const activePopups = activePopupSelector(state);
    if (!isEmpty(activePopups)) return;

    const activeModalId = activeModalIdSelector(state);
    if (activeModalId) return;

    // If currently editing elements, then do nothing
    const editingId = getCurrentlyEditingId(state);
    if (editingId) return;

    const selectedElements = getSelectedElements(state);
    if (selectedElements.size !== 1) return;

    const selectedElement = selectedElements.first();

    // Enter can be used for a few different things in table elements, so prevent the default behavior
    if (isTable(selectedElement)) return;

    dispatch(handleElementEdit(selectedElement));
};

const createCardInColumn = (element) => (dispatch, getState) => {
    const elementId = getElementId(element);

    const state = getState();

    const currentBoardId = getCurrentBoardIdFromState(state);

    dispatch(
        createAndEditElement({
            creationSource: ELEMENT_CREATION_SOURCES.CMD_ENTER,
            elementType: ElementType.CARD_TYPE,
            location: {
                parentId: elementId,
                section: BoardSections.INBOX,
                position: {
                    index: 0,
                },
            },
            currentBoardId,
            content: {},
            select: true,
        }),
    );
};

const startEditingTaskTitle = (element) => (dispatch, getState) => {
    const elementId = getElementId(element);
    const isAlreadyShowingTitle = getShowTitle(element);

    if (!isAlreadyShowingTitle) {
        dispatch(
            updateSelectedElements({
                changes: {
                    [SHOW_TITLE_PROPERTY]: true,
                },
            }),
        );
    }

    dispatch(
        startEditingElementWithDelay({
            id: elementId,
            editorId: getMainEditorId({ element }),
            editorKey: getMainEditorKey({ element }),
            editDelay: 0,
        }),
    );
};

const handleElementSecondaryAction = (element) => (dispatch, getState) => {
    const elementType = getElementType(element);

    switch (elementType) {
        // Images should open their modal
        case ElementType.COLUMN_TYPE:
            return dispatch(createCardInColumn(element));
        // Tasks should start editing their title
        case ElementType.TASK_LIST_TYPE:
            return dispatch(startEditingTaskTitle(element));
        default:
            return null;
    }
};

/**
 * Handles alt+return for the different element types.
 */
export const handleElementSecondaryReturnAction = () => (dispatch, getState) => {
    const state = getState();

    const activePopups = activePopupSelector(state);
    if (!isEmpty(activePopups)) return;

    const activeModalId = activeModalIdSelector(state);
    if (activeModalId) return;

    const selectedElements = getSelectedElements(state);
    if (selectedElements.size !== 1) return;

    const selectedElement = selectedElements.first();

    return dispatch(handleElementSecondaryAction(selectedElement));
};
